/**
 * ChangeStageDialog — council "set stage to anything with a reason".
 *
 * Different from the per-stage action buttons (Post for Applications,
 * Mark Staffed, …). Those follow the linear lifecycle; this one is
 * the escape hatch for "I need to send this back to under_review" or
 * "we mis-classified this, it should be staffed already". The audit
 * trail captures the reason via the `notes` field on the transition.
 *
 * For resolution targets, redirects callers to the existing Resolve
 * dialog — that flow asks for a resolution_type which this dialog
 * doesn't try to replicate.
 */

import { ToldLine } from '../../components/ToldLine'
import { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Label,
  Option,
  Radio,
  RadioGroup,
  Textarea,
  tokens,
} from '@fluentui/react-components'
import type { DemandStage } from '../../types/demand'
import { faAudienceOption, reachLabel, type BroadcastAudience } from '../../components/TransitionDialog'
import { useBroadcastReach } from '../../hooks/useDemands'
import { stageLabel } from '../../lib/labels'
import { suggestedNext } from '../../lib/stageHints'
import { bucketColors } from '../../theme'

// Labels come from the canonical map — an inline literal here is how the
// "Parking" / "Parking Lot" drift got in (see lib/labels.ts, issue #772).
const STAGES: { key: DemandStage; label: string }[] = (
  ['scoping', 'triaging', 'resourcing', 'parking'] as const
).map((key) => ({ key, label: stageLabel(key) }))

/**
 * The suggested next stages now live in lib/stageHints, shared with the
 * Kanban's drop-zone tint. This file used to hold a private copy, and the
 * two copies had drifted apart on their legacy rows — rows the backend's
 * own table never had. See that module.
 *
 * Still not a gate: every target in the picker is reachable, off-path ones
 * just ask why, and that reason lands in the audit trail (#1049).
 */


interface ChangeStageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentStage: DemandStage
  focusAreaCode?: string
  /** Needed for the audience reach preview — see useBroadcastReach. */
  demandId?: number
  onConfirm: (
    toStage: DemandStage,
    reason: string,
    broadcastAudience?: BroadcastAudience,
  ) => void
  onOpenResolve?: () => void
  pending?: boolean
}

export function ChangeStageDialog({
  open,
  onOpenChange,
  currentStage,
  focusAreaCode,
  demandId,
  onConfirm,
  onOpenResolve,
  pending,
}: ChangeStageDialogProps) {
  const [toStage, setToStage] = useState<DemandStage | ''>('')
  const [reason, setReason] = useState('')
  const [audience, setAudience] = useState<BroadcastAudience>('fa_talent')

  const allowedNatural = useMemo(() => suggestedNext(currentStage), [currentStage])
  const requiresOverride = useMemo(
    () => !!toStage && toStage !== currentStage && !allowedNatural.includes(toStage),
    [toStage, currentStage, allowedNatural],
  )
  // Posting into Resourcing is the "open for applications" moment — offer the
  // same talent-broadcast selector the TransitionDialog shows on that step so
  // this escape-hatch path also fans out notifications.
  const showBroadcast = toStage === 'resourcing'
  // Declared after `showBroadcast` because it reads it. Fetched only while this
  // dialog is open on the posting step.
  const { data: reach } = useBroadcastReach(demandId, open && showBroadcast)
  const faOption = faAudienceOption(focusAreaCode, reach)
  // The default is `fa_talent`, which on a BO or UN demand is a dead choice.
  // Move off it once the reach lands rather than letting Confirm send nothing.
  useEffect(() => {
    if (!faOption.applicable && audience === 'fa_talent') setAudience('all_talent')
  }, [faOption.applicable, audience])

  const canConfirm =
    !!toStage &&
    toStage !== currentStage &&
    (!requiresOverride || reason.trim().length > 0)

  function handleConfirm() {
    if (!toStage || !canConfirm) return
    onConfirm(toStage, reason.trim(), showBroadcast ? audience : undefined)
  }

  // Reset state when the dialog closes.
  function handleOpenChange(next: boolean) {
    if (!next) {
      setToStage('')
      setReason('')
      setAudience('fa_talent')
    }
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={(_, d) => handleOpenChange(d.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Change stage</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
              <div>
                <Caption1 style={{ display: 'block', marginBottom: 4 }}>
                  Current: <strong>{stageLabel(currentStage)}</strong>
                </Caption1>
                <Caption1 style={{ display: 'block', marginBottom: 6 }}>Move to</Caption1>
                <Dropdown
                  value={
                    toStage ? STAGES.find((s) => s.key === toStage)?.label ?? '' : ''
                  }
                  placeholder="Pick a stage…"
                  selectedOptions={toStage ? [toStage] : []}
                  onOptionSelect={(_, d) => {
                    const v = d.optionValue as DemandStage | undefined
                    setToStage(v ?? '')
                  }}
                  aria-label="Target stage"
                >
                  {STAGES.filter((s) => s.key !== currentStage).map((s) => (
                    <Option key={s.key} value={s.key}>
                      {s.label}
                    </Option>
                  ))}
                </Dropdown>
              </div>

              <div>
                <Caption1 style={{ display: 'block', marginBottom: 6 }}>
                  Reason {requiresOverride && <strong style={{ color: bucketColors.highPriority }}>(required)</strong>}
                </Caption1>
                <Textarea
                  value={reason}
                  onChange={(_, d) => setReason(d.value)}
                  rows={3}
                  resize="vertical"
                  placeholder={
                    requiresOverride
                      ? 'Why are you forcing this transition? Logged in the audit trail.'
                      : 'Optional note about the change.'
                  }
                  aria-label="Reason"
                />
                {requiresOverride && (
                  <Caption1
                    style={{
                      display: 'block',
                      marginTop: 6,
                      color: bucketColors.highPriority,
                      fontWeight: 600,
                    }}
                  >
                    Non-standard transition — a reason is required and will appear in the demand's
                    stage history.
                  </Caption1>
                )}
              </div>

              {showBroadcast && (
                <div style={{ padding: '12px', background: '#fffbeb', borderRadius: '6px', border: '1px solid #fde68a' }}>
                  <Label weight="semibold" style={{ display: 'block', marginBottom: '8px' }}>
                    Notify talents about this opportunity?
                  </Label>
                  <RadioGroup value={audience} onChange={(_e, data) => setAudience(data.value as BroadcastAudience)}>
                    <Radio
                      value="fa_talent"
                      disabled={!faOption.applicable}
                      label={faOption.label}
                    />
                    <Radio
                      value="all_talent"
                      label={`All talents across DSAI${reachLabel(reach?.all_talent)}`}
                    />
                    <Radio value="none" label="Don't notify anyone" />
                  </RadioGroup>
                  {reach && faOption.applicable && audience !== 'none' &&
                    reach[audience === 'all_talent' ? 'all_talent' : 'fa_talent'] === 0 && (
                      <Caption1 style={{ display: 'block', marginTop: '8px', color: '#a8442f' }}>
                        Nobody matches this audience — the demand would go live with no one told.
                      </Caption1>
                    )}
                </div>
              )}

              {!!toStage && !showBroadcast && <ToldLine>nobody</ToldLine>}

              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                Need to mark this resolved instead?{' '}
                <Button
                  appearance="transparent"
                  size="small"
                  onClick={() => {
                    handleOpenChange(false)
                    onOpenResolve?.()
                  }}
                >
                  Open Resolve…
                </Button>
              </Caption1>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleConfirm}
              disabled={!canConfirm || pending}
            >
              {pending ? 'Saving…' : 'Change stage'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  )
}
